/**
 * Helpers voor canvas-focus-navigatie (M6.5-W11, slices B + C).
 *
 * Focus kan op drie soorten items vallen: een concept-instance (node),
 * een relatie (edge), of een edge-label. Het focus-id is uniform een
 * string, met een type-discriminator in het `kind`-veld:
 *
 *  - node:          `id` = viewState-key van de concept-instance
 *  - edge:          `id` = relation-id
 *  - edge-label:    `id` = `"label:<relationId>:<pos>"` met
 *                   `pos ∈ {start, middle, end}`. Sharp concept-model /
 *                   detailed-concept-views hebben start + end (forward/
 *                   reverse-label); andere views hebben alleen middle.
 *
 * Twee algoritmes:
 *  - `getNextFocusInVisualOrder` — lineaire Tab-cycle, sorteert op
 *    (y, x) met 20px-rijtolerantie zodat licht-verschoven items niet
 *    achterstevoren cyclen.
 *  - `findGeometricNeighbor` — fallback voor Arrow wanneer geen
 *    "natuurlijke" volgende stap te vinden is: 90°-cone in richting,
 *    afstand × alignment als score, fallback nearest-overall.
 *
 * De rijke Arrow-state-machine (label-eerst, dan relation, dan node)
 * leeft in `findArrowFlowNeighbor` — komt in slice C.
 */

export type FocusableKind = "node" | "edge" | "edge-label";
export type EdgeLabelPos = "start" | "middle" | "end";

export interface Focusable {
  /** Uniforme string-id zoals beschreven in de module-doc. */
  id: string;
  kind: FocusableKind;
  /** Center-X in flow-coords (absoluut). */
  cx: number;
  /** Center-Y in flow-coords. */
  cy: number;
  /**
   * Bounding-box-breedte/-hoogte in flow-coords. Voor cel-overlap-tests:
   * een cursor op de rand of titel van een grote container moet die
   * container WEL detecteren, ook al ligt z'n center elders. Voor
   * nodes = `width × height`; voor edges = bbox over source/target;
   * voor labels = kleine ~40 × 18 default.
   */
  bboxW?: number;
  bboxH?: number;
  /**
   * Visuele oppervlakte (w × h voor nodes). Wordt gebruikt om bij
   * meerdere hits in dezelfde cel de meest-specifieke (kleinste-area)
   * te kiezen — analoog aan muis-klik-hit-testing waar 't bovenste
   * geneste item wint.
   */
  area?: number;
  /** Voor edges + edge-labels: de onderliggende relation-id. */
  relationId?: string;
  /** Voor edge-labels: positie langs de lijn. */
  labelPos?: EdgeLabelPos;
  /** Voor edges: source/target-info om de richting van de relatie te
   *  bepalen tijdens Arrow-flow-navigatie. */
  sourceNodeId?: string;
  targetNodeId?: string;
  sourceCx?: number;
  sourceCy?: number;
  targetCx?: number;
  targetCy?: number;
}

/** Encodeer een edge-label-focus-id (deterministisch, parseable). */
export function edgeLabelFocusId(relationId: string, pos: EdgeLabelPos): string {
  return `label:${relationId}:${pos}`;
}

/** Decodeer een edge-label-focus-id; returnt `null` voor niet-label-ids. */
export function parseEdgeLabelFocusId(
  id: string,
): { relationId: string; pos: EdgeLabelPos } | null {
  if (!id.startsWith("label:")) return null;
  const parts = id.split(":");
  if (parts.length !== 3) return null;
  const relationId = parts[1];
  const pos = parts[2];
  if (!relationId || (pos !== "start" && pos !== "middle" && pos !== "end")) return null;
  return { relationId, pos };
}

/** Tolerantie (px) waarbinnen items als "dezelfde rij" tellen. Genest
 *  items hebben overlappende y-centers; de tolerantie is groot genoeg
 *  om die in dezelfde rij te groeperen maar klein genoeg om naast
 *  elkaar liggende items in verschillende rijen te houden. */
const ROW_TOLERANCE = 20;

/** Sorteer focusables in lees-volgorde:
 *  1. Y (top → bottom, met ROW_TOLERANCE zodat items op vergelijkbare
 *     hoogte in dezelfde rij vallen)
 *  2. Binnen rij: area aflopend — grote container vóór z'n inhoud
 *  3. Bij gelijke area: X (links → rechts)
 *  Deterministisch, lineair, geen oscillatie. */
export function sortInReadingOrder(focusables: Focusable[]): Focusable[] {
  return [...focusables].sort((a, b) => {
    const dy = a.cy - b.cy;
    if (Math.abs(dy) > ROW_TOLERANCE) return dy;
    const areaA = a.area ?? 0;
    const areaB = b.area ?? 0;
    if (areaA !== areaB) return areaB - areaA;
    return a.cx - b.cx;
  });
}

/** Tab / Shift+Tab cycle in lees-volgorde. Wrap-around aan beide kanten. */
export function getNextFocusInVisualOrder(
  focusables: Focusable[],
  currentId: string | null,
  direction: 1 | -1,
): string | null {
  if (focusables.length === 0) return null;
  const sorted = sortInReadingOrder(focusables);
  if (!currentId) {
    const first = sorted[0];
    return first ? first.id : null;
  }
  const idx = sorted.findIndex((f) => f.id === currentId);
  if (idx === -1) {
    const fallback = direction > 0 ? sorted[0] : sorted[sorted.length - 1];
    return fallback ? fallback.id : null;
  }
  const next = (idx + direction + sorted.length) % sorted.length;
  const target = sorted[next];
  return target ? target.id : null;
}

/** Lijn (P1→P2) snijdt rechthoek? Liang-Barsky-clipping; returnt true
 *  als enig deel van de lijn binnen of op de rechthoek-rand ligt.
 *  Gebruikt voor edge-cel-detectie zodat een diagonale relatie niet
 *  ten onrechte hits oplevert op cellen ver naast de visuele lijn. */
function lineIntersectsRect(
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  rectLeft: number,
  rectRight: number,
  rectTop: number,
  rectBottom: number,
): boolean {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  let tmin = 0;
  let tmax = 1;
  const clip = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0; // parallel aan as
    const t = q / p;
    if (p < 0) {
      if (t > tmax) return false;
      if (t > tmin) tmin = t;
    } else {
      if (t < tmin) return false;
      if (t < tmax) tmax = t;
    }
    return true;
  };
  if (!clip(-dx, p1.x - rectLeft)) return false;
  if (!clip(dx, rectRight - p1.x)) return false;
  if (!clip(-dy, p1.y - rectTop)) return false;
  if (!clip(dy, rectBottom - p1.y)) return false;
  return true;
}

/** Items die de cursor-cel raken — analoog aan muis-klik hit-testing.
 *  - Nodes en labels: bbox-overlap (rechthoekige item-vlak ∩ cel-vlak).
 *  - Edges: echte lijn-cel-intersectie via Liang-Barsky, anders zou
 *    een diagonale relatie z'n hele bounding-box-rechthoek opslokken.
 *  Sort op area OPLOPEND: meest-specifieke (kleinste) item wint, net
 *  als een muis-klik op een geneste container het inner-item kiest. */
export function focusablesInCell(
  focusables: Focusable[],
  cell: { x: number; y: number },
  cellSize: number,
): Focusable[] {
  const cellLeft = cell.x;
  const cellRight = cell.x + cellSize;
  const cellTop = cell.y;
  const cellBottom = cell.y + cellSize;
  const inCell = focusables.filter((f) => {
    if (
      f.kind === "edge" &&
      f.sourceCx !== undefined &&
      f.sourceCy !== undefined &&
      f.targetCx !== undefined &&
      f.targetCy !== undefined
    ) {
      return lineIntersectsRect(
        { x: f.sourceCx, y: f.sourceCy },
        { x: f.targetCx, y: f.targetCy },
        cellLeft,
        cellRight,
        cellTop,
        cellBottom,
      );
    }
    const w = f.bboxW ?? 0;
    const h = f.bboxH ?? 0;
    const itemLeft = f.cx - w / 2;
    const itemRight = f.cx + w / 2;
    const itemTop = f.cy - h / 2;
    const itemBottom = f.cy + h / 2;
    return (
      itemLeft < cellRight && itemRight > cellLeft && itemTop < cellBottom && itemBottom > cellTop
    );
  });
  return inCell.sort((a, b) => (a.area ?? 0) - (b.area ?? 0));
}

/** Snap een willekeurig (x, y)-punt naar de top-left van z'n
 *  bevattende cel. */
export function snapToCell(
  point: { x: number; y: number },
  cellSize: number,
): { x: number; y: number } {
  return {
    x: Math.floor(point.x / cellSize) * cellSize,
    y: Math.floor(point.y / cellSize) * cellSize,
  };
}

/** Arrow Down / Up — navigeer naar de eerstvolgende rij in de
 *  lees-volgorde. "Eerstvolgende rij" = eerste item met `|cy - current.cy|
 *  > ROW_TOLERANCE` in pijl-richting (omlaag of omhoog). Wrap-around
 *  vanaf het einde naar het begin, en omgekeerd. */
export function getNextRowInReadingOrder(
  focusables: Focusable[],
  currentId: string | null,
  direction: "down" | "up",
): string | null {
  if (focusables.length === 0) return null;
  const sorted = sortInReadingOrder(focusables);
  if (!currentId) {
    const first = sorted[0];
    return first ? first.id : null;
  }
  const idx = sorted.findIndex((f) => f.id === currentId);
  if (idx === -1) return sorted[0]?.id ?? null;
  const current = sorted[idx];
  if (!current) return null;
  if (direction === "down") {
    for (let i = idx + 1; i < sorted.length; i++) {
      const c = sorted[i];
      if (c && c.cy > current.cy + ROW_TOLERANCE) return c.id;
    }
    // Wrap: ga naar de bovenste rij (eerste item dat strikt hoger ligt
    // dan current — komt overeen met "rond gegaan").
    for (let i = 0; i < idx; i++) {
      const c = sorted[i];
      if (c && c.cy < current.cy - ROW_TOLERANCE) return c.id;
    }
    return null;
  }
  // direction === "up"
  for (let i = idx - 1; i >= 0; i--) {
    const c = sorted[i];
    if (c && c.cy < current.cy - ROW_TOLERANCE) return c.id;
  }
  // Wrap: ga naar de onderste rij.
  for (let i = sorted.length - 1; i > idx; i--) {
    const c = sorted[i];
    if (c && c.cy > current.cy + ROW_TOLERANCE) return c.id;
  }
  return null;
}

export type Direction = "up" | "down" | "left" | "right";

/** Geometrische buurman binnen een 90°-cone. Fallback: dichtstbijzijnde
 *  überhaupt wanneer geen kandidaat in cone valt. */
export function findGeometricNeighbor(
  focusables: Focusable[],
  currentId: string,
  direction: Direction,
): string | null {
  const current = focusables.find((f) => f.id === currentId);
  if (!current) return null;
  const others = focusables.filter((f) => f.id !== currentId);
  if (others.length === 0) return null;

  const dir = directionVector(direction);

  let best: { id: string; score: number } | null = null;
  let nearestOverall: { id: string; dist: number } | null = null;

  for (const f of others) {
    const dx = f.cx - current.cx;
    const dy = f.cy - current.cy;
    const dist = Math.hypot(dx, dy);
    if (dist === 0) continue;
    if (!nearestOverall || dist < nearestOverall.dist) {
      nearestOverall = { id: f.id, dist };
    }
    const alignment = (dx * dir.x + dy * dir.y) / dist;
    // cos(45°) = √2/2 — alleen kandidaten binnen een 45°-cone rond
    // de richting overwegen, anders ratelen we van side-richting naar
    // onverwachte buren.
    if (alignment < Math.SQRT1_2) continue;
    const score = dist * (2 - alignment);
    if (!best || score < best.score) {
      best = { id: f.id, score };
    }
  }
  if (best) return best.id;
  return nearestOverall ? nearestOverall.id : null;
}

function directionVector(direction: Direction): { x: number; y: number } {
  switch (direction) {
    case "up":
      return { x: 0, y: -1 };
    case "down":
      return { x: 0, y: 1 };
    case "left":
      return { x: -1, y: 0 };
    case "right":
      return { x: 1, y: 0 };
  }
}

/**
 * Arrow-flow state-machine (Gerald 2026-05-13). De Arrow-toets navigeert
 * niet alleen geometrisch naar de dichtstbijzijnde buurman, maar volgt
 * de semantische lees-flow van een diagram.
 *
 * **Diagonale lijnen worden gesnapt naar hun dominante as** (`|dx| ≥
 * |dy|` → horizontaal, anders verticaal). Zonder die snap ontstaat een
 * lock-in waar dezelfde 45°-edge zowel als horizontaal als verticaal
 * "aligned" telt — een Arrow Down zou dan dezelfde target geven als
 * Arrow Right, en de gebruiker komt vast te zitten.
 *
 * - Focus op een **node**: zoek relaties die VAN deze node af (of er
 *   naartoe) lopen, met als snap-as gelijk aan de pijl-as. Heeft de
 *   gekozen relatie een label-aan-begin (start- of middle-label) →
 *   label krijgt focus, anders → relatie krijgt focus.
 * - Focus op een **relatie**: pijl-as gelijk aan relatie-as forward
 *   → end-label of end-object. Pijl-as gelijk aan relatie-as backward
 *   → start-label of source-object.
 * - Focus op een **label**: middle-label → relatie. Start-label +
 *   forward → relatie. End-label + forward → end-object. Backward-
 *   bewegingen symmetrisch.
 *
 * In alle drie gevallen: **als pijl-as ≠ relatie-as** (dwars), val
 * terug op een geometrische cone-zoek die labels uitsluit — Gerald:
 * "label krijgt alleen focus als je beweegt in de richting van de
 * relatie".
 */
type FlowDir = "forward" | "backward" | "perpendicular";

/** Snap een edge tot z'n dominante as (horizontaal of verticaal) en
 *  vergelijk met de pijl-richting. Returnt forward (edge wijst in pijl-
 *  richting), backward (tegen) of perpendicular (dwars-as). */
function edgeFlowDirection(edge: Focusable, arrow: Direction): FlowDir {
  if (
    edge.sourceCx === undefined ||
    edge.sourceCy === undefined ||
    edge.targetCx === undefined ||
    edge.targetCy === undefined
  )
    return "perpendicular";
  const dx = edge.targetCx - edge.sourceCx;
  const dy = edge.targetCy - edge.sourceCy;
  if (dx === 0 && dy === 0) return "perpendicular";
  const arrowAxis: "h" | "v" = arrow === "up" || arrow === "down" ? "v" : "h";
  const edgeAxis: "h" | "v" = Math.abs(dx) >= Math.abs(dy) ? "h" : "v";
  if (arrowAxis !== edgeAxis) return "perpendicular";
  if (edgeAxis === "h") {
    const edgePointsRight = dx > 0;
    const arrowPointsRight = arrow === "right";
    return edgePointsRight === arrowPointsRight ? "forward" : "backward";
  }
  // Vertical edge axis.
  const edgePointsDown = dy > 0;
  const arrowPointsDown = arrow === "down";
  return edgePointsDown === arrowPointsDown ? "forward" : "backward";
}

export function findArrowFlowNeighbor(
  focusables: Focusable[],
  currentId: string,
  direction: Direction,
): string | null {
  const current = focusables.find((f) => f.id === currentId);
  if (!current) return null;

  // Bouw lookup-maps voor O(1)-acces tijdens evaluatie.
  const byId = new Map<string, Focusable>();
  const labelsByRelation = new Map<string, Focusable[]>();
  const outgoingByNode = new Map<string, Focusable[]>(); // edges-met-source==node
  const incomingByNode = new Map<string, Focusable[]>(); // edges-met-target==node
  for (const f of focusables) {
    byId.set(f.id, f);
    if (f.kind === "edge-label" && f.relationId) {
      const list = labelsByRelation.get(f.relationId) ?? [];
      list.push(f);
      labelsByRelation.set(f.relationId, list);
    }
    if (f.kind === "edge") {
      if (f.sourceNodeId) {
        const list = outgoingByNode.get(f.sourceNodeId) ?? [];
        list.push(f);
        outgoingByNode.set(f.sourceNodeId, list);
      }
      if (f.targetNodeId) {
        const list = incomingByNode.get(f.targetNodeId) ?? [];
        list.push(f);
        incomingByNode.set(f.targetNodeId, list);
      }
    }
  }

  /** Geef start-label terug als 'r één is, anders middle-label, anders
   *  null. (Voor non-Sharp views is middle = de enige label.) */
  const labelAtStart = (relationId: string): Focusable | null => {
    const labels = labelsByRelation.get(relationId) ?? [];
    return (
      labels.find((l) => l.labelPos === "start") ??
      labels.find((l) => l.labelPos === "middle") ??
      null
    );
  };
  const labelAtEnd = (relationId: string): Focusable | null => {
    const labels = labelsByRelation.get(relationId) ?? [];
    return (
      labels.find((l) => l.labelPos === "end") ??
      labels.find((l) => l.labelPos === "middle") ??
      null
    );
  };

  // ── CASE 1: focus op een node ─────────────────────────────────────
  if (current.kind === "node") {
    // Relaties die VAN deze node af in de pijl-richting lopen.
    // Source = current → richting source→target = pijl-richting.
    const outgoing = outgoingByNode.get(current.id) ?? [];
    // Ook incoming meenemen maar dan andersom: pijl van current naar
    // de andere endpoint is de relatie tegen z'n eigen as in.
    const incoming = incomingByNode.get(current.id) ?? [];
    type Candidate = { edge: Focusable; reversed: boolean };
    const candidates: Candidate[] = [];
    for (const e of outgoing) {
      if (edgeFlowDirection(e, direction) === "forward") {
        candidates.push({ edge: e, reversed: false });
      }
    }
    for (const e of incoming) {
      // Vanuit current (target) naar source is forward voor user als
      // edge "backward" wijst t.o.v. pijl.
      if (edgeFlowDirection(e, direction) === "backward") {
        candidates.push({ edge: e, reversed: true });
      }
    }
    if (candidates.length > 0) {
      // Bij meerdere kandidaten: pak de eerste (deterministisch via
      // bouw-volgorde). Een latere refinement kan kijken naar tussen-
      // hoek voor "meest recht voor je", maar na snapping zijn ze
      // allemaal langs dezelfde as → equally valid.
      const best = candidates[0];
      if (!best) return findGeometricNeighborExcludingLabels(focusables, currentId, direction);
      // Label-aan-begin van de relatie?
      const label = labelAtStart(best.edge.id);
      if (label) return label.id;
      return best.edge.id;
    }
    // Geen relatie-vanuit-deze-node in pijl-richting: fallback naar
    // dichtstbijzijnde node/relatie (geen labels).
    return findGeometricNeighborExcludingLabels(focusables, currentId, direction);
  }

  // ── CASE 2: focus op een relatie ──────────────────────────────────
  if (current.kind === "edge") {
    const flow = edgeFlowDirection(current, direction);
    if (flow === "forward") {
      const label = labelAtEnd(current.id);
      if (label) return label.id;
      if (current.targetNodeId && byId.has(current.targetNodeId)) return current.targetNodeId;
    } else if (flow === "backward") {
      const label = labelAtStart(current.id);
      if (label) return label.id;
      if (current.sourceNodeId && byId.has(current.sourceNodeId)) return current.sourceNodeId;
    }
    return findGeometricNeighborExcludingLabels(focusables, currentId, direction);
  }

  // ── CASE 3: focus op een edge-label ───────────────────────────────
  if (current.kind === "edge-label" && current.relationId) {
    const parentEdge = byId.get(current.relationId);
    if (parentEdge?.kind !== "edge") {
      return findGeometricNeighborExcludingLabels(focusables, currentId, direction);
    }
    const flow = edgeFlowDirection(parentEdge, direction);
    if (flow === "forward") {
      if (current.labelPos === "middle" || current.labelPos === "start") {
        return parentEdge.id;
      }
      if (parentEdge.targetNodeId && byId.has(parentEdge.targetNodeId)) {
        return parentEdge.targetNodeId;
      }
    } else if (flow === "backward") {
      if (current.labelPos === "middle" || current.labelPos === "end") {
        return parentEdge.id;
      }
      if (parentEdge.sourceNodeId && byId.has(parentEdge.sourceNodeId)) {
        return parentEdge.sourceNodeId;
      }
    }
    return findGeometricNeighborExcludingLabels(focusables, currentId, direction);
  }

  return null;
}

/** Fallback bij dwars-beweging: geometrische cone-zoek die labels
 *  uitsluit (Gerald: "label krijgt alleen focus als je beweegt in de
 *  richting van de relatie"). */
function findGeometricNeighborExcludingLabels(
  focusables: Focusable[],
  currentId: string,
  direction: Direction,
): string | null {
  const filtered = focusables.filter((f) => f.kind !== "edge-label");
  // Houd de current in de filtered-set zodat findGeometricNeighbor het
  // referentie-punt vindt; als 't 'n label is, dat zal hij niet — dan
  // val terug op de oorspronkelijke set zonder labels.
  if (!filtered.some((f) => f.id === currentId)) {
    const current = focusables.find((f) => f.id === currentId);
    if (current) filtered.push(current);
  }
  return findGeometricNeighbor(filtered, currentId, direction);
}
